'use client';

import React, { useState, useEffect, useCallback, createContext, useContext, useRef } from 'react';

// ─── TYPES ────────────────────────────────────────────────────────────────────

export type ToastType = 'success' | 'info' | 'warning' | 'error';

export interface ToastAction {
  label: string;
  onClick: () => void;
  style?: 'primary' | 'secondary';
}

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number; // ms, 0 = no auto-dismiss
  actions?: ToastAction[];
  icon?: string;
}

export interface ConfirmDialogOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel?: () => void;
}

interface ToastContextValue {
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  showConfirm: (options: ConfirmDialogOptions) => void;
  // Convenience helpers
  toastObservationSaved: (observationTitle?: string, actions?: ToastAction[]) => void;
  toastDepthUnlocked: (layerName: string, layerNumber: number) => void;
  toastCollaborationInvite: (inviterName: string, ayatRef: string, onAccept: () => void, onDecline: () => void) => void;
  toastSuccess: (title: string, message?: string) => void;
  toastError: (title: string, message?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

// ─── TOAST COLORS ─────────────────────────────────────────────────────────────

const TOAST_CONFIG: Record<ToastType, { border: string; icon: string; iconColor: string; bg: string }> = {
  success: { border: 'rgba(45,122,71,0.5)', icon: '✓', iconColor: '#2d7a47', bg: 'rgba(45,122,71,0.08)' },
  info:    { border: 'rgba(45,138,135,0.5)', icon: '◎', iconColor: '#2d8a87', bg: 'rgba(45,138,135,0.08)' },
  warning: { border: 'rgba(201,168,76,0.5)', icon: '⚠', iconColor: '#c9a84c', bg: 'rgba(201,168,76,0.08)' },
  error:   { border: 'rgba(180,60,60,0.5)', icon: '✕', iconColor: '#b43c3c', bg: 'rgba(180,60,60,0.08)' },
};

// ─── SINGLE TOAST ITEM ────────────────────────────────────────────────────────

function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: (id: string) => void }) {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cfg = TOAST_CONFIG[toast.type];

  const dismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => onRemove(toast.id), 300);
  }, [toast.id, onRemove]);

  useEffect(() => {
    // Animate in
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const duration = toast.duration ?? 4500;
    if (duration > 0) {
      timerRef.current = setTimeout(dismiss, duration);
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [toast.duration, dismiss]);

  return (
    <div
      style={{
        background: '#0a1a14',
        border: `1px solid ${cfg.border}`,
        borderLeft: `3px solid ${cfg.iconColor}`,
        borderRadius: 8,
        padding: '12px 14px',
        minWidth: 280,
        maxWidth: 380,
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        transform: visible && !exiting ? 'translateX(0) scale(1)' : 'translateX(100%) scale(0.95)',
        opacity: visible && !exiting ? 1 : 0,
        transition: 'transform 0.3s cubic-bezier(0.22,1,0.36,1), opacity 0.3s ease',
        pointerEvents: 'all',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Progress bar */}
      {(toast.duration ?? 4500) > 0 && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: 2,
          background: `${cfg.iconColor}20`,
        }}>
          <div style={{
            height: '100%',
            background: cfg.iconColor,
            animation: `toast-progress ${toast.duration ?? 4500}ms linear forwards`,
          }} />
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        {/* Icon */}
        <div style={{
          width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
          background: cfg.bg, border: `1px solid ${cfg.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, color: cfg.iconColor, fontWeight: 'bold',
        }}>
          {toast.icon || cfg.icon}
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: '#F5F0E8', fontFamily: 'monospace', letterSpacing: '.04em', marginBottom: toast.message ? 3 : 0 }}>
            {toast.title}
          </p>
          {toast.message && (
            <p style={{ fontSize: 11, color: 'rgba(212,197,169,0.6)', lineHeight: 1.4 }}>
              {toast.message}
            </p>
          )}
          {toast.actions && toast.actions.length > 0 && (
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              {toast.actions.map((action, i) => (
                <button
                  key={i}
                  onClick={() => { action.onClick(); dismiss(); }}
                  style={{
                    fontSize: 9, fontFamily: 'monospace', letterSpacing: '.08em',
                    textTransform: 'uppercase', padding: '4px 10px', borderRadius: 4,
                    cursor: 'pointer', transition: 'all 0.15s',
                    background: action.style === 'primary' ? cfg.iconColor : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${action.style === 'primary' ? cfg.iconColor : 'rgba(255,255,255,0.1)'}`,
                    color: action.style === 'primary' ? '#060d12' : 'rgba(212,197,169,0.7)',
                  }}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Close */}
        <button
          onClick={dismiss}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'rgba(212,197,169,0.3)', fontSize: 16, lineHeight: 1,
            padding: '0 2px', flexShrink: 0, transition: 'color 0.15s',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(212,197,169,0.7)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(212,197,169,0.3)'; }}
        >
          ×
        </button>
      </div>
    </div>
  );
}

// ─── CONFIRM DIALOG ───────────────────────────────────────────────────────────

function ConfirmDialog({ options, onClose }: { options: ConfirmDialogOptions; onClose: () => void }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  function handleConfirm() {
    setVisible(false);
    setTimeout(() => { options.onConfirm(); onClose(); }, 200);
  }

  function handleCancel() {
    setVisible(false);
    setTimeout(() => { options.onCancel?.(); onClose(); }, 200);
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 600,
        background: 'rgba(6,13,18,0.85)',
        backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '0 16px',
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.2s ease',
      }}
      onClick={handleCancel}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#0a1a14',
          border: `1px solid ${options.danger ? 'rgba(180,60,60,0.4)' : 'rgba(201,168,76,0.25)'}`,
          borderRadius: 10,
          padding: '28px 24px',
          maxWidth: 400, width: '100%',
          boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
          transform: visible ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(8px)',
          transition: 'transform 0.25s cubic-bezier(0.22,1,0.36,1)',
        }}
      >
        {/* Icon */}
        <div style={{
          width: 40, height: 40, borderRadius: '50%', margin: '0 auto 16px',
          background: options.danger ? 'rgba(180,60,60,0.1)' : 'rgba(201,168,76,0.08)',
          border: `1px solid ${options.danger ? 'rgba(180,60,60,0.3)' : 'rgba(201,168,76,0.2)'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18, color: options.danger ? '#b43c3c' : '#c9a84c',
        }}>
          {options.danger ? '⚠' : '◎'}
        </div>

        <h3 style={{
          fontFamily: 'monospace', fontSize: 14, fontWeight: 900,
          textTransform: 'uppercase', letterSpacing: '.06em',
          color: '#F5F0E8', textAlign: 'center', marginBottom: 10,
        }}>
          {options.title}
        </h3>
        <p style={{
          fontSize: 13, color: 'rgba(212,197,169,0.6)',
          lineHeight: 1.6, textAlign: 'center', marginBottom: 24,
        }}>
          {options.message}
        </p>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button
            onClick={handleCancel}
            style={{
              flex: 1, padding: '10px 16px',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 6, cursor: 'pointer',
              fontFamily: 'monospace', fontSize: 10,
              textTransform: 'uppercase', letterSpacing: '.08em',
              color: 'rgba(212,197,169,0.6)',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.08)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.04)'; }}
          >
            {options.cancelLabel || 'Cancel'}
          </button>
          <button
            onClick={handleConfirm}
            style={{
              flex: 1, padding: '10px 16px',
              background: options.danger ? 'rgba(180,60,60,0.15)' : 'rgba(201,168,76,0.12)',
              border: `1px solid ${options.danger ? 'rgba(180,60,60,0.4)' : 'rgba(201,168,76,0.35)'}`,
              borderRadius: 6, cursor: 'pointer',
              fontFamily: 'monospace', fontSize: 10,
              textTransform: 'uppercase', letterSpacing: '.08em',
              color: options.danger ? '#b43c3c' : '#c9a84c',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLButtonElement).style.background = options.danger ? 'rgba(180,60,60,0.25)' : 'rgba(201,168,76,0.2)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.background = options.danger ? 'rgba(180,60,60,0.15)' : 'rgba(201,168,76,0.12)';
            }}
          >
            {options.confirmLabel || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── PROVIDER ─────────────────────────────────────────────────────────────────

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogOptions | null>(null);

  const addToast = useCallback((toast: Omit<Toast, 'id'>): string => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setToasts(prev => [...prev.slice(-4), { ...toast, id }]); // max 5 toasts
    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showConfirm = useCallback((options: ConfirmDialogOptions) => {
    setConfirmDialog(options);
  }, []);

  // Convenience helpers
  const toastObservationSaved = useCallback((observationTitle?: string, actions?: ToastAction[]) => {
    addToast({
      type: 'success',
      icon: '◎',
      title: 'Observation Saved',
      message: observationTitle ? `"${observationTitle}" added to your chain` : 'Your observation has been recorded',
      duration: 4000,
      actions: actions || [
        { label: 'View Chain', onClick: () => {}, style: 'primary' },
      ],
    });
  }, [addToast]);

  const toastDepthUnlocked = useCallback((layerName: string, layerNumber: number) => {
    addToast({
      type: 'info',
      icon: '◈',
      title: `Batin Layer ${layerNumber} Unlocked`,
      message: `${layerName} — a new depth of recognition revealed`,
      duration: 5000,
      actions: [
        { label: 'Explore Layer', onClick: () => {}, style: 'primary' },
      ],
    });
  }, [addToast]);

  const toastCollaborationInvite = useCallback((inviterName: string, ayatRef: string, onAccept: () => void, onDecline: () => void) => {
    addToast({
      type: 'info',
      icon: '⌘',
      title: 'Collaboration Invite',
      message: `${inviterName} invites you to annotate ${ayatRef}`,
      duration: 0, // no auto-dismiss for invites
      actions: [
        { label: 'Accept', onClick: onAccept, style: 'primary' },
        { label: 'Decline', onClick: onDecline },
      ],
    });
  }, [addToast]);

  const toastSuccess = useCallback((title: string, message?: string) => {
    addToast({ type: 'success', title, message, duration: 3500 });
  }, [addToast]);

  const toastError = useCallback((title: string, message?: string) => {
    addToast({ type: 'error', title, message, duration: 5000 });
  }, [addToast]);

  const value: ToastContextValue = {
    addToast, removeToast, showConfirm,
    toastObservationSaved, toastDepthUnlocked, toastCollaborationInvite,
    toastSuccess, toastError,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Toast container */}
      <div
        style={{
          position: 'fixed', bottom: 24, right: 24,
          zIndex: 500, display: 'flex', flexDirection: 'column',
          gap: 8, alignItems: 'flex-end', pointerEvents: 'none',
        }}
      >
        <style>{`
          @keyframes toast-progress {
            from { width: 100%; }
            to { width: 0%; }
          }
        `}</style>
        {toasts.map(toast => (
          <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </div>
      {/* Confirm dialog */}
      {confirmDialog && (
        <ConfirmDialog
          options={confirmDialog}
          onClose={() => setConfirmDialog(null)}
        />
      )}
    </ToastContext.Provider>
  );
}
